import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { ChevronRight, Edit, Home, Plus, Search, Trash2 } from "lucide-react";

type CustomerVehicle = {
  vehicleNo: string;
  vehicleType: string;
};

const VEHICLE_TYPES = [
  "Ambulance",
  "Auto",
  "Barrel",
  "Bike",
  "Bus",
  "Cab",
  "Can",
  "Car",
  "Eicher",
  "JCB",
  "Jeep",
  "Mini Lorry",
  "Sumo",
  "Tractor",
  "Truck",
  "Van",
];

const normalizeVehicles = (customer: any): CustomerVehicle[] => {
  if (Array.isArray(customer?.vehicles) && customer.vehicles.length > 0) {
    return customer.vehicles.map((vehicle: any) => ({
      vehicleNo: vehicle.vehicleNo || vehicle.vehicle_no || "",
      vehicleType: vehicle.vehicleType || vehicle.vehicle_type || "Car",
    }));
  }

  if (customer?.vehicle_no || customer?.vehicle_type) {
    return [{
      vehicleNo: customer.vehicle_no || "",
      vehicleType: customer.vehicle_type || "Car",
    }];
  }

  return [];
};

export default function CreditCustomerVehicles() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [customer, setCustomer] = useState<any | null>(null);
  const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formVehicle, setFormVehicle] = useState<CustomerVehicle>({ vehicleNo: "", vehicleType: "Ambulance" });
  const [showCount, setShowCount] = useState("All");
  const [filter, setFilter] = useState("");

  const fetchCustomer = useCallback(async () => {
    const res = await fetch("/api/credit-customers", { credentials: "include" });
    const data = await res.json();

    if (!data.ok) {
      toast({ title: "Error", description: data.error || "Failed to fetch customers", variant: "destructive" });
      return;
    }

    const found = (data.rows || []).find((row: any) => row.id === customerId);
    if (!found) {
      toast({ title: "Error", description: "Customer not found", variant: "destructive" });
      navigate("/master/credit-customer");
      return;
    }

    setCustomer(found);
    setVehicles(normalizeVehicles(found));
  }, [customerId, navigate, toast]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const filteredVehicles = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const rows = vehicles.map((vehicle, index) => ({ vehicle, index }));
    const filteredRows = !query
      ? rows
      : rows.filter(({ vehicle }) => (
        vehicle.vehicleNo.toLowerCase().includes(query) ||
        vehicle.vehicleType.toLowerCase().includes(query)
      ));

    return showCount === "All" ? filteredRows : filteredRows.slice(0, Number(showCount));
  }, [filter, showCount, vehicles]);

  const openAddDialog = () => {
    setEditingIndex(null);
    setFormVehicle({ vehicleNo: "", vehicleType: "Ambulance" });
    setDialogOpen(true);
  };

  const openEditDialog = (index: number) => {
    setEditingIndex(index);
    setFormVehicle(vehicles[index] || { vehicleNo: "", vehicleType: "Car" });
    setDialogOpen(true);
  };

  const saveVehicles = async (nextVehicles: CustomerVehicle[]) => {
    if (!customer) return;

    const primaryVehicle = nextVehicles[0] || { vehicleNo: "", vehicleType: "" };
    const res = await fetch(`/api/credit-customers/${customer.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        vehicles: nextVehicles,
        vehicle_no: primaryVehicle.vehicleNo,
        vehicle_type: primaryVehicle.vehicleType,
      }),
    });

    const result = await res.json();
    if (result.ok) {
      setVehicles(nextVehicles);
      toast({ title: "Success", description: "Vehicles updated successfully" });
      fetchCustomer();
    } else {
      toast({ title: "Error", description: result.error || "Failed to update vehicles", variant: "destructive" });
    }
  };

  const submitVehicle = async () => {
    const cleanedVehicle = {
      vehicleNo: formVehicle.vehicleNo.trim(),
      vehicleType: formVehicle.vehicleType || "Car",
    };

    const nextVehicles = editingIndex === null
      ? [...vehicles, cleanedVehicle]
      : vehicles.map((vehicle, index) => index === editingIndex ? cleanedVehicle : vehicle);

    await saveVehicles(nextVehicles);
    setDialogOpen(false);
  };

  const deleteVehicle = async (index: number) => {
    await saveVehicles(vehicles.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800">
        <Home className="h-4 w-4 text-slate-900" />
        <span>Master</span>
        <ChevronRight className="h-3 w-3" />
        <span>Customer Entry</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-normal">Cust Vehicle</span>
      </div>

      <div className="rounded bg-gradient-to-r from-blue-600 to-blue-800 p-6">
        <div className="grid grid-cols-3 gap-8">
          <div className="grid grid-cols-[150px_1fr] items-center gap-3">
            <Label className="text-xs font-semibold text-white">Organization Name :</Label>
            <Input readOnly value={customer?.organization_name || ""} className="h-8 bg-white text-xs text-black" />
          </div>
          <div className="grid grid-cols-[80px_1fr] items-center gap-3">
            <Label className="text-xs font-semibold text-white">Name :</Label>
            <Input readOnly value={customer?.representative_name || customer?.organization_name || ""} className="h-8 bg-white text-xs text-black" />
          </div>
          <div className="grid grid-cols-[80px_1fr] items-center gap-3">
            <Label className="text-xs font-semibold text-white">Phone</Label>
            <Input readOnly value={customer?.mobile_number || customer?.phone_number || ""} className="h-8 bg-white text-xs text-black" />
          </div>
        </div>
      </div>

      <div>
        <Button type="button" className="h-8 bg-green-600 px-4 text-xs hover:bg-green-700" onClick={openAddDialog}>
          <Plus className="h-3 w-3" />
          Add Vehicle
        </Button>
      </div>

      <div className="flex items-center justify-between border-b pb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs">Show:</span>
          <Select value={showCount} onValueChange={setShowCount}>
            <SelectTrigger className="h-9 w-20 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="All">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" className="h-8 border-green-500 px-3 text-xs font-semibold text-green-600">CSV</Button>
          <Button type="button" variant="outline" className="h-8 border-red-500 px-3 text-xs font-semibold text-red-600">PDF</Button>
          <span className="text-xs">Filter:</span>
          <div className="relative">
            <Input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Type to filter..." className="h-9 w-56 pr-8 text-xs" />
            <Search className="absolute right-2 top-2.5 h-4 w-4 text-slate-500" />
          </div>
        </div>
      </div>

      <div className="min-h-[300px] border-b border-black">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-9 w-24 text-xs font-bold">S.No</TableHead>
              <TableHead className="h-9 text-xs font-bold">Vehicle No</TableHead>
              <TableHead className="h-9 text-xs font-bold">Vehicle Type</TableHead>
              <TableHead className="h-9 text-center text-xs font-bold">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredVehicles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-xs text-slate-500">No vehicles found.</TableCell>
              </TableRow>
            ) : (
              filteredVehicles.map(({ vehicle, index }, displayIndex) => (
                <TableRow key={`${vehicle.vehicleNo}-${index}`} className="bg-slate-100">
                  <TableCell className="text-xs">{displayIndex + 1}</TableCell>
                  <TableCell className="text-xs">{vehicle.vehicleNo || "0"}</TableCell>
                  <TableCell className="text-xs">{vehicle.vehicleType || "Vehicle"}</TableCell>
                  <TableCell className="text-center">
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-green-700" onClick={() => openEditDialog(index)} title="Edit vehicle">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteVehicle(index)} title="Delete vehicle">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-1 text-xs">
        <span>Showing {filteredVehicles.length ? 1 : 0} to {filteredVehicles.length} of {filteredVehicles.length} entries</span>
        <div className="flex items-center gap-3">
          <span>&larr;</span>
          <span className="border px-3 py-2">1</span>
          <span>&rarr;</span>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl p-0 overflow-hidden">
          <DialogHeader className="bg-blue-700 px-5 py-3 text-white">
            <DialogTitle className="text-base">Add Vehicles</DialogTitle>
            <DialogDescription className="text-blue-100">
              {customer?.organization_name || ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3 p-5">
            <div>
              <Label className="text-xs font-semibold">Vehicle No</Label>
              <Input value={formVehicle.vehicleNo} onChange={(event) => setFormVehicle((current) => ({ ...current, vehicleNo: event.target.value }))} placeholder="Vehicle No" className="mt-1 h-9 text-xs" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Vehicle Type</Label>
              <Select value={formVehicle.vehicleType} onValueChange={(value) => setFormVehicle((current) => ({ ...current, vehicleType: value }))}>
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={openAddDialog} title="Reset vehicle form">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex justify-center gap-2 pb-5">
            <Button type="button" className="h-8 bg-green-600 px-5 text-xs hover:bg-green-700" onClick={submitVehicle}>Submit</Button>
            <Button type="button" className="h-8 bg-red-600 px-5 text-xs hover:bg-red-700" onClick={() => setDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
